import React, {useState, useRef, useEffect} from "react";
import {GrClose} from "react-icons/gr";
import {AiOutlineEye} from "react-icons/ai";
import {ScaleLoader} from "react-spinners";
import {toast} from "react-toastify";
import PropTypes from "prop-types";
import Api from "../../Api/api";
import {useStateValue} from "../../states/StateProvider";
import moment from "moment/moment";

const ServiceMailModal = ({company, companyId, user, portal}) => {
    const [{supportModalOpen}, dispatch] = useStateValue();

    const [formData, setFormData] = useState({
        message: "",
        fpName: company,
        link: window.location.href,
        absender: user.email,
        empfanger: "",
    });

    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPreview, setShowPreview] = useState(false);
    const [milestones, setMilestones] = useState([]);
    const [substeps, setSubsteps] = useState([]);
    const [selectedMilestone, setSelectedMilestone] = useState("");
    const [selectedSubstep, setSelectedSubstep] = useState("");
    const [milestonesLoading, setMilestonesLoading] = useState(false);
    const [substepsLoading, setSubstepsLoading] = useState(false);
    const [{ noteSent }] = useStateValue();
    const textareaRef = useRef(null);

    const placeholders = [
        { value: "{Tage}", label: "{Tage}" },
        { value: "{Firmenprojekt}", label: "{Firmenprojekt}" },
        { value: "{Link}", label: "{Link}" }
    ];

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
        setErrors({...errors, [name]: ""});
    };

    const handlePlaceholderSelect = (e) => {
        const placeholder = e.target.value;
        if (placeholder) {
            setFormData({
                ...formData,
                message: formData.message + placeholder + " "
            });
            e.target.value = "";

            // Focus textarea and position cursor at the end
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    };

    const getPreviewText = () => {
        const currentDate = moment().format("DD.MM.YYYY");
        return formData.message
            .replace(/\{Meilenstein\}/g, "meilenstein")
            .replace(/\{Schritt\}/g, "step")
            .replace(/\{Tage\}/g, currentDate)
            .replace(/\{Firmenprojekt\}/g, company)
            .replace(/\{Link\}/g, window.location.href);
    };

    const handlePreview = () => {
        setShowPreview(true);
    };

    const fetchMilestones = async () => {
        setMilestonesLoading(true);
        try {
            const response = await Api().get(`/milestones/${portal}/${companyId}`);
            setMilestones(response.data.tabs || []);
        } catch (error) {
            toast.error('Meilensteine konnten nicht geladen werden!');
            console.error('Error fetching milestones:', error);
        } finally {
            setMilestonesLoading(false);
        }
    };

    const fetchSubsteps = async (milestoneId) => {
        setSubstepsLoading(true);
        try {
            const response = await Api().get(`/sub-steps/${portal}/${milestoneId}/${companyId}`);
            setSubsteps(response.data.subSteps || []);
        } catch (error) {
            toast.error('Schritte konnten nicht geladen werden!');
            console.error('Error fetching substeps:', error);
        } finally {
            setSubstepsLoading(false);
        }
    };

    useEffect(() => {
        if (companyId && portal) {
            fetchMilestones();
        }
    }, [companyId, portal]);

    const handleMilestoneSelect = (e) => {
        const milestoneId = e.target.value;
        const milestone = milestones.find(m => m.milestoneID === milestoneId);

        if (milestoneId && milestone) {
            setSelectedMilestone(milestoneId);
            setSelectedSubstep("");
            setSubsteps([]);

            // Add milestone to textarea
            setFormData({
                ...formData,
                message: formData.message + milestone.milestoneLabel + " "
            });

            // Focus textarea at the end
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);

            // Fetch substeps
            fetchSubsteps(milestoneId);
        }
    };

    const handleSubstepSelect = (e) => {
        const substepId = e.target.value;
        const substep = substeps.find(s => s.substepID === substepId);

        if (substepId && substep) {
            setSelectedSubstep(substepId);

            // Add substep to textarea
            setFormData({
                ...formData,
                message: formData.message + substep.stepName + " "
            });

            // Focus textarea at the end
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    const length = textareaRef.current.value.length;
                    textareaRef.current.setSelectionRange(length, length);
                }
            }, 0);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.empfanger) {
            newErrors.empfanger = "Bitte geben Sie Empfänger E-Mail-Adresse ein.";
        } else if (!/\S+@\S+\.\S+/.test(formData.empfanger)) {
            newErrors.empfanger = "Ungültige Empfänger E-Mail-Adresse.";
        }

        if (!formData.absender) {
            newErrors.absender = "Bitte geben Sie Ihre E-Mail-Adresse ein.";
        } else if (!/\S+@\S+\.\S+/.test(formData.absender)) {
            newErrors.absender = "Ungültige Absender E-Mail-Adresse.";
        }
        if (!formData.message) newErrors.message = "Bitte geben Sie Ihre Nachricht ein.";
        if (!formData.fpName) newErrors.fpName = "Bitte geben Sie FP Name.";
        if (!formData.link) newErrors.link = "Bitte geben Sie ein Link zu FP Seite.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        try {
            const emailData = {
                ...formData,
                message: getPreviewText()
            };
            const response = await Api().post("/serviceMail", emailData);
            if (response.status === 200) {
                const currentDateTime = moment().format("DD.MM.YYYY HH:mm");
                const noteText = `Servicemail an ${formData.empfanger} von ${formData.absender} \n Inhalt: ${getPreviewText()}`;

                const formDataNote = new FormData();
                formDataNote.append("note", noteText);
                formDataNote.append("userID", user.ID);
                formDataNote.append("firma", companyId);
                formDataNote.append("portal", portal);
                formDataNote.append("art", "AutoFP");

                try {
                    Api().post('/sendNote', formDataNote).then(() => {
                        dispatch({ type: "SET_NOTE_SENT", item: !noteSent });
                        setLoading(false);
                    });
                } catch (error) {
                    toast.error("Fehler beim Speichern der automatischen Bemerkung");
                }

                setFormData({
                    message: "", fpName: company, link: window.location.href, absender: user.email, empfanger: "",
                });
                setErrors({});
                setSubmitted(true);
                toast.success("Ihre Nachricht wurde erfolgreich gesendet.");
            } else {
                toast.error("Fehler beim Senden der Nachricht.");
            }
        } catch (error) {
            toast.error("Fehler beim Senden der Nachricht.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (<div className="fixed inset-0 z-[60] flex items-center justify-center bg-text bg-opacity-50">
            <div className="relative w-full max-w-xl bg-white p-6 rounded-lg shadow-lg mx-4 max-h-[90vh] overflow-y-auto">
                <GrClose
                    className="cursor-pointer absolute top-4 right-4"
                    size="22px"
                    onClick={() => dispatch({type: "SET_SUPPORT_MODAL_OPEN", item: false})}
                />

                {submitted ? (
                    <>
                        <h2 className="font-bold text-xl mb-2">Service-Mail Innendienst</h2>
                        <div className="text-green-700 font-medium text-lg">
                            Die E-Mail wurde erfolgreich versandt.
                        </div>
                    </>
                ) : (
                    <>
                        <h2 className="font-bold text-xl mb-2">Aufforderung zur Projektaktualisierung an Closer</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium">FP Name</label>
                                <input
                                    type="text"
                                    name="fpName"
                                    value={formData.fpName}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded"
                                />
                                {errors.fpName && <p className="text-red-500 text-xs mt-1">{errors.fpName}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Link</label>
                                <input
                                    type="text"
                                    name="link"
                                    value={formData.link}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded"
                                />
                                {errors.link && <p className="text-red-500 text-xs mt-1">{errors.link}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Absender</label>
                                <input
                                    type="text"
                                    name="absender"
                                    value={formData.absender}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded"
                                />
                                {errors.absender && <p className="text-red-500 text-xs mt-1">{errors.absender}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Empfänger</label>
                                <input
                                    type="text"
                                    name="empfanger"
                                    value={formData.empfanger}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded"
                                />
                                {errors.empfanger && <p className="text-red-500 text-xs mt-1">{errors.empfanger}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Platzhalter</label>
                                <select
                                    onChange={handlePlaceholderSelect}
                                    className="w-full p-2 border rounded mb-2"
                                    defaultValue=""
                                >
                                    <option value="">-- Platzhalter auswählen --</option>
                                    {placeholders.map((placeholder, index) => (
                                        <option key={index} value={placeholder.value}>
                                            {placeholder.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Meilenstein Platzhalter</label>
                                <select
                                    value={selectedMilestone}
                                    onChange={handleMilestoneSelect}
                                    disabled={milestonesLoading}
                                    className="w-full p-2 border rounded mb-2"
                                >
                                    <option value="">
                                        {milestonesLoading ? "Lade Meilensteine..." : "-- Meilenstein auswählen --"}
                                    </option>
                                    {milestones.map((milestone) => (
                                        <option key={milestone.milestoneID} value={milestone.milestoneID}>
                                            {milestone.milestoneLabel}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Schritt Platzhalter</label>
                                <select
                                    value={selectedSubstep}
                                    onChange={handleSubstepSelect}
                                    disabled={substepsLoading || !selectedMilestone}
                                    className="w-full p-2 border rounded mb-2"
                                >
                                    <option value="">
                                        {substepsLoading
                                            ? "Lade Schritte..."
                                            : !selectedMilestone
                                                ? "Zuerst Meilenstein auswählen"
                                                : "-- Schritt auswählen --"}
                                    </option>
                                    {substeps.map((substep) => (
                                        <option key={substep.substepID} value={substep.substepID}>
                                            {substep.stepName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Aufforderungs-Text</label>
                                <textarea
                                    ref={textareaRef}
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="4"
                                    className="w-full p-2 border rounded"
                                />
                                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handlePreview}
                                    className="py-2 px-4 border border-gray-500 text-gray-500 rounded hover:bg-mainBlue hover:text-white hover:border-mainBlue inline-flex items-center"
                                    title="Vorschau"
                                >
                                    <AiOutlineEye size="18px" />
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`py-2 px-4 rounded flex-1 text-white ${
                                        loading ? 'bg-mainBlue opacity-50 cursor-not-allowed' : 'bg-mainBlue'
                                    }`}
                                >
                                    {loading ? <ScaleLoader height={12} color="#ffffff" /> : 'Aufforderung senden'}
                                </button>
                            </div>
                        </form>
                    </>
                )}

            </div>

            {showPreview && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-text bg-opacity-70"
                     onClick={() => setShowPreview(false)}>
                    <div className="relative w-full max-w-lg bg-white p-6 rounded-lg shadow-lg mx-4"
                         onClick={(e) => e.stopPropagation()}>
                        <GrClose
                            className="cursor-pointer absolute top-4 right-4"
                            size="22px"
                            onClick={() => setShowPreview(false)}
                        />
                        <h2 className="font-bold text-xl mb-4">Vorschau</h2>
                        <div className="whitespace-pre-wrap border rounded p-4 bg-white max-h-96 overflow-y-auto">
                            {getPreviewText()}
                        </div>
                    </div>
                </div>
            )}
        </div>);
};

ServiceMailModal.propTypes = {
    company: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    user: PropTypes.number.isRequired,
    portal: PropTypes.string.isRequired,
};

export default ServiceMailModal;
